import { config } from "@constants/config";
import cn from "classnames";

import React, { useEffect, useRef, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import Input from ".";
import type { IAutocompleteInputProps } from "../components.types";
import { useField } from "formik";
import { useDebounce } from "@hooks";

import styles from "./styles.module.scss";
import { Dots } from "@ui/Loader";

const autocompleteOptions: google.maps.places.AutocompletionRequest = {
  types: [
    "point_of_interest",
    "premise",
    "subpremise",
    "route",
    "street_number"
  ],
  componentRestrictions: { country: "AU" },
  input: ""
};

const AutocompleteInput = ({
  name,
  tag,
  onChoose,
  error,
  scrollOnError,
  ...rest
}: IAutocompleteInputProps): JSX.Element => {
  const Tag = tag ?? Input;

  const ref = useRef<HTMLInputElement>();
  // eslint-disable-next-line
  const [field, meta, helpers] = useField(name);
  const [search, setSearch] = useState<string>(
    field.value?.formatted_address ?? field.value ?? ""
  );

  const [show, setShow] = useState<boolean>(false);

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading
  } = usePlacesService({
    apiKey: config.google_key,

    language: "en-AU",
    libraries: ["places"]
    // options: autocompleteOptions
  });

  const clearValue = (): void => {
    helpers.setValue(null);
    setSearch("");
  };

  const onFocus = (): void => {
    setShow(true);
  };
  const onBlur = (): void => {
    setShow(false);
  };

  const onItemClick = (
    place: google.maps.places.AutocompletePrediction
  ): void => {
    setShow(false);

    placesService?.getDetails(
      {
        placeId: place.place_id ?? "",
        fields: [
          "name",
          "geometry.location",
          "place_id",
          "formatted_address",
          "address_components",
          "utc_offset",
          "type"
        ]
      },
      (placeDetails) => {
        if (!placeDetails) return;
        const result = {
          ...placeDetails,
          formatted_address: place.description?.replace(", Australia", "") ?? ""
        };

        setSearch(result.formatted_address);
        if (typeof onChoose === "function") {
          onChoose(result, name);
        }
        helpers.setValue(result);
      }
    );
  };

  useDebounce(() => {
    if (search && search !== (field.value?.formatted_address ?? field.value)) {
      getPlacePredictions({ ...autocompleteOptions, input: search });
    }
    if (!search) {
      helpers.setValue(null);
    }
  }, search);

  useEffect(() => {
    if (error && scrollOnError) {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  useEffect(() => {
    setSearch(field.value?.formatted_address ?? field.value ?? "");
  }, [field.value?.formatted_address]);

  return (
    <Tag
      ref={ref}
      name={name}
      id={name}
      value={search}
      setValue={setSearch}
      placeholder=""
      clearFormikValue={clearValue}
      {...rest}
      error={
        tag
          ? meta.error ?? error
          : (Boolean(meta.error) && meta.touched) || error
      }
      touched={meta.touched}
      autocomplete
      onFocus={onFocus}
      onBlur={onBlur}
      select={
        <div className={cn(styles.select, show && styles.show)}>
          {isPlacePredictionsLoading ? (
            <Dots padding />
          ) : (
            <div className={styles.select__list}>
              {placePredictions.map((place) => (
                <div
                  className={styles["select__list-item"]}
                  key={place.place_id}
                  onMouseDown={(e) => {
                    onItemClick(place);
                  }}
                >
                  {place.description.replace(", Australia", "")}
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
};

export default AutocompleteInput;
